import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { setResponseHeaders } from "@tanstack/react-start/server";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { talks } from "@/lib/talks";

const KNOWN_VIDEO_IDS = new Set(talks.map((t) => t.id));

export type TranscriptSegment = { t: number; d: number; text: string };
export type TranscriptRow = {
  videoId: string;
  lang: string;
  isAuto: boolean;
  segments: TranscriptSegment[];
  plainText: string;
  status: "ok" | "unavailable" | "error";
  fetchedAt: string;
};

const WATCH_UA =
  "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.0 Safari/605.1.15";

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, "&")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)));
}

/**
 * Fetch YouTube auto-captions for a video. Strategy:
 * 1. Fetch the watch HTML, extract ytInitialPlayerResponse.
 * 2. Find the caption track for the preferred language (or any auto track).
 * 3. Fetch the timedtext URL with &fmt=json3 and parse events.
 * Returns status='unavailable' when no captions exist; never throws.
 */
async function fetchYoutubeTranscript(
  videoId: string,
  prefLang = "tr",
): Promise<{ segments: TranscriptSegment[]; lang: string; isAuto: boolean; status: "ok" | "unavailable" }> {
  try {
    const watch = await fetch(`https://www.youtube.com/watch?v=${videoId}&hl=${prefLang}`, {
      headers: { "User-Agent": WATCH_UA, "Accept-Language": `${prefLang},en;q=0.5` },
    });
    if (!watch.ok) return { segments: [], lang: prefLang, isAuto: true, status: "unavailable" };
    const html = await watch.text();

    // Extract captionTracks array
    const m = html.match(/"captionTracks":(\[[^\]]+\])/);
    if (!m) return { segments: [], lang: prefLang, isAuto: true, status: "unavailable" };

    type Track = { baseUrl: string; languageCode: string; kind?: string; name?: { simpleText?: string } };
    let tracks: Track[];
    try {
      tracks = JSON.parse(m[1]);
    } catch {
      return { segments: [], lang: prefLang, isAuto: true, status: "unavailable" };
    }
    if (!tracks.length) return { segments: [], lang: prefLang, isAuto: true, status: "unavailable" };

    const pick =
      tracks.find((t) => t.languageCode === prefLang && t.kind !== "asr") ||
      tracks.find((t) => t.languageCode === prefLang) ||
      tracks.find((t) => t.languageCode.startsWith(prefLang)) ||
      tracks[0];

    const url = new URL(pick.baseUrl.replace(/\\u0026/g, "&"));
    url.searchParams.set("fmt", "json3");
    const tt = await fetch(url.toString(), { headers: { "User-Agent": WATCH_UA } });
    if (!tt.ok) return { segments: [], lang: pick.languageCode, isAuto: pick.kind === "asr", status: "unavailable" };
    const json = (await tt.json()) as {
      events?: Array<{ tStartMs?: number; dDurationMs?: number; segs?: Array<{ utf8?: string }> }>;
    };
    const segments: TranscriptSegment[] = [];
    for (const ev of json.events ?? []) {
      if (!ev.segs) continue;
      const text = ev.segs
        .map((s) => s.utf8 ?? "")
        .join("")
        .replace(/\n/g, " ")
        .trim();
      if (!text) continue;
      segments.push({
        t: Math.round((ev.tStartMs ?? 0) / 1000),
        d: Math.round((ev.dDurationMs ?? 0) / 1000),
        text: decodeEntities(text),
      });
    }
    return {
      segments,
      lang: pick.languageCode,
      isAuto: pick.kind === "asr",
      status: segments.length ? "ok" : "unavailable",
    };
  } catch (e) {
    console.error("yt transcript fetch failed", videoId, e);
    return { segments: [], lang: prefLang, isAuto: true, status: "unavailable" };
  }
}

const videoIdSchema = z
  .object({
    videoId: z.string().min(8).max(20).regex(/^[A-Za-z0-9_-]+$/),
    lang: z.string().min(2).max(5).optional(),
  })
  .strict();

/**
 * Get a transcript: serves from cache if present, otherwise fetches from
 * YouTube once and persists. Always returns a row (status indicates result).
 */
export const getTranscript = createServerFn({ method: "GET" })
  .inputValidator((d: { videoId: string; lang?: string }) => videoIdSchema.parse(d))
  .handler(async ({ data }): Promise<TranscriptRow> => {
    setResponseHeaders(
      new Headers({
        "Cache-Control": "public, max-age=600, s-maxage=86400, stale-while-revalidate=604800",
      }),
    );

    const { videoId } = data;
    if (!KNOWN_VIDEO_IDS.has(videoId)) throw new Error("Unknown talk");
    const prefLang = data.lang === "en" ? "en" : "tr";

    const { data: cached } = await supabaseAdmin
      .from("talk_transcripts")
      .select("*")
      .eq("video_id", videoId)
      .maybeSingle();

    if (cached) {
      return {
        videoId: cached.video_id,
        lang: cached.lang,
        isAuto: cached.is_auto,
        segments: (cached.segments as TranscriptSegment[]) ?? [],
        plainText: cached.plain_text,
        status: cached.status as TranscriptRow["status"],
        fetchedAt: cached.fetched_at,
      };
    }

    const fetched = await fetchYoutubeTranscript(videoId, prefLang);
    const plainText = fetched.segments.map((s) => s.text).join(" ");

    const row = {
      video_id: videoId,
      lang: fetched.lang,
      is_auto: fetched.isAuto,
      segments: fetched.segments,
      plain_text: plainText,
      status: fetched.status,
      fetched_at: new Date().toISOString(),
    };

    await supabaseAdmin.from("talk_transcripts").upsert(row, { onConflict: "video_id" });

    return {
      videoId,
      lang: fetched.lang,
      isAuto: fetched.isAuto,
      segments: fetched.segments,
      plainText,
      status: fetched.status,
      fetchedAt: row.fetched_at,
    };
  });
